import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { ROUTES } from '@app/definitions/constants/routes.constants';
import { NotificationType } from '@app/definitions/enums/notifications/notification-type.enum';
import { ProcessTablesFilter } from '@app/definitions/enums/process-tables/filters-with-dropdown.enum';
import { processTablesNames } from '@app/definitions/enums/process-tables/process-tables-names.enum';
import { ProcessStatuses } from '@app/definitions/enums/replenishment-process/process-status';
import { ProcessType } from '@app/definitions/enums/replenishment-process/process-type.enum';
import { NotificationData } from '@app/definitions/interfaces/notifications/notification-data';
import { UserConfigsStoreFacade } from '@app/stores/global-store/user-configs/user-configs-store.facade';
import { NotificationsStoreService } from '@app/stores/signal-stores/notifications-store/notifications-store.service';
import {
  PreselectedTableFiltersStore
} from '@app/stores/signal-stores/table-preselected-filters-store/table-preselected-filters-store.service';

@Injectable(
  { providedIn: 'root' }
)
export class NotificationNavigationService {
  private readonly tablePreselectedFiltersStore = inject(PreselectedTableFiltersStore);
  private readonly router = inject(Router);
  private readonly userConfigsFacade = inject(UserConfigsStoreFacade);

  private navigationMap = new Map<NotificationType, { tableName: string; status?: string; type?: string }>([
    [ NotificationType.NEWLY_CREATED_PURCHASE_EXISTS, {
      type: ProcessType.PURCHASE,
      tableName: processTablesNames.PURCHASE_INTERNAL,
    }],
    [ NotificationType.APPROVED_PROCESS_EXISTS, {
      tableName: processTablesNames.APPROVED_INTERNAL,
      status: ProcessStatuses.APPROVED,
    }],
    [ NotificationType.REJECTED_PROCESS_EXISTS, {
      tableName: processTablesNames.REJECTED_INTERNAL,
      status: ProcessStatuses.REJECTED,
    }],
    [ NotificationType.APPROVAL_REQUIRED_PROCESS_EXISTS, {
      tableName: processTablesNames.APPROVAL_REQUIRED_EXTERNAL,
      status: ProcessStatuses.APPROVAL_REQUIRED,
    }],
    [ NotificationType.DELAYED_PROCESS, {
      tableName: this.userConfigsFacade.isInternalViewSignal()
        ? processTablesNames.ALL_INTERNAL
        : processTablesNames.ALL_EXTERNAL,
    }],
    [ NotificationType.ERP_ORDER_NOT_FOUND, {
      tableName: processTablesNames.ALL_INTERNAL
    }]
  ]);

  private preselectedFiltersMap = new Map<NotificationType,
    (notification: NotificationData) => void>([
    [ NotificationType.NEWLY_CREATED_PURCHASE_EXISTS, (notification) =>
      this.tablePreselectedFiltersStore.setPreselectedTableFilters([
        { filterName: ProcessTablesFilter.customerName, values: [ notification.customer?.name ?? '' ]},
        { filterName: ProcessTablesFilter.status, values: [ ProcessStatuses.NEW ]}
      ])
    ],
    [ NotificationType.APPROVED_PROCESS_EXISTS, (notification) =>
      this.tablePreselectedFiltersStore.setPreselectedTableFilters([
        { filterName: ProcessTablesFilter.customerName, values: [ notification.customer?.name ?? '' ]},
      ])
    ],
    [ NotificationType.REJECTED_PROCESS_EXISTS, (notification) =>
      this.tablePreselectedFiltersStore.setPreselectedTableFilters([
        { filterName: ProcessTablesFilter.customerName, values: [ notification.customer?.name ?? '' ]},
      ])
    ],
    [ NotificationType.DELAYED_PROCESS, (notification: NotificationData) =>
      this.tablePreselectedFiltersStore.setPreselectedTableFilters([
        { filterName: ProcessTablesFilter.processId, values: [ notification.content?.replenishmentProcess?.processId ?? '' ]},
      ])
    ],
    [ NotificationType.ERP_ORDER_NOT_FOUND, (notification: NotificationData) =>
      this.tablePreselectedFiltersStore.setPreselectedTableFilters([
        { filterName: ProcessTablesFilter.processId, values: notification.content?.replenishmentProcessIds ?? []},
      ])
    ]
  ]);

  private notificationStore = inject(NotificationsStoreService);

  public navigateToTable(notification: NotificationData): void {
    this.tablePreselectedFiltersStore.clearPreselectedTableFilters();
    this.preselectedFiltersMap.get(notification.notificationType)?.(notification);
    this.tablePreselectedFiltersStore.lockFilters();

    const baseParams = this.navigationMap.get(notification.notificationType);

    if  (!baseParams) return;

    // Random navigationId added to force redrawing the table and apply preselected filters each time
    const params = { ...baseParams, navigationId: crypto.randomUUID() };

    this.notificationStore.updateNotification({ notificationId: notification.id, isRead: true });

    this.router.navigate([ ROUTES.REPLENISHMENT, ROUTES.PROCESSES ], { queryParams: params });
  }
}
